import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CATEGORIES, STATES, categoryLabel, stateLabel, formatEventDate } from "../lib/categories";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Pencil, Trash2, X, Users, Calendar } from "lucide-react";

const EMPTY = {
  title: "",
  category: "church_workshop",
  state: "",
  event_date: "",
  start_time: "",
  end_time: "",
  location: "",
  description: "",
};

export default function Admin() {
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [ev, bk] = await Promise.all([api.get("/events"), api.get("/admin/bookings")]);
      setEvents(ev.data);
      setBookings(bk.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openCreate = () => {
    setForm(EMPTY);
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setForm({
      title: ev.title,
      category: ev.category,
      state: ev.state || "",
      event_date: ev.event_date,
      start_time: ev.start_time,
      end_time: ev.end_time || "",
      location: ev.location,
      description: ev.description || "",
    });
    setEditId(ev.id);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/events/${editId}`, form);
        toast.success("Event updated.");
      } else {
        await api.post("/events", form);
        toast.success("Event added to the schedule.");
      }
      setShowForm(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success("Event deleted.");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-white flex flex-col">
      <Navbar />

      <section className="border-b border-white/10 grid-lines">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline text-[hsl(var(--primary))] mb-3">Command · Admin</div>
            <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">
              CONTROL ROOM
            </h1>
          </div>
          <button
            data-testid="new-event-btn"
            onClick={openCreate}
            className="flex items-center gap-2 bg-[hsl(var(--primary))] text-white overline px-6 py-4 hard-shadow-hover"
          >
            <Plus className="w-4 h-4" /> New Event
          </button>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#1A1A1A]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex">
          {[
            { k: "events", label: "Events", icon: Calendar },
            { k: "bookings", label: "Bookings & Reps", icon: Users },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                data-testid={`tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`flex items-center gap-2 overline px-5 py-4 border-b-2 transition-colors ${
                  tab === t.k
                    ? "border-[hsl(var(--primary))] text-white"
                    : "border-transparent text-white/50 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="flex-1 max-w-[1400px] w-full mx-auto px-5 sm:px-8 py-10">
        {tab === "events" && (
          <div className="border border-white/10">
            {events.length === 0 ? (
              <div className="py-24 text-center text-white/40">
                No events yet. Hit “New Event” to build the schedule.
              </div>
            ) : (
              events.map((ev) => (
                <div
                  key={ev.id}
                  data-testid={`admin-event-${ev.id}`}
                  className="flex flex-wrap items-center gap-4 border-b border-white/10 last:border-b-0 px-5 py-4 hover:bg-white/5 transition-colors"
                >
                  <div className="w-28 overline text-[hsl(var(--primary))]">
                    {categoryLabel(ev.category)}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-heading font-bold">{ev.title}</div>
                    <div className="text-white/50 text-sm">
                      {stateLabel(ev.state)} · {formatEventDate(ev.event_date)} · {ev.start_time} · {ev.location}
                    </div>
                  </div>
                  <span
                    className={`overline px-3 py-1 border ${
                      ev.is_booked
                        ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                        : "border-white/15 text-white/50"
                    }`}
                  >
                    {ev.is_booked ? "Booked" : "Open"}
                  </span>
                  <div className="flex gap-2">
                    <button
                      data-testid={`edit-event-${ev.id}`}
                      onClick={() => openEdit(ev)}
                      className="p-2 border border-white/15 hover:border-white/50 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-event-${ev.id}`}
                      onClick={() => remove(ev)}
                      className="p-2 border border-white/15 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div className="border border-white/10">
            <div className="px-5 py-3 border-b border-white/10 overline text-white/40 grid grid-cols-12 gap-2">
              <div className="col-span-4">Event</div>
              <div className="col-span-3">Date</div>
              <div className="col-span-3">Booked By (Rep)</div>
              <div className="col-span-2">Phone</div>
            </div>
            {bookings.length === 0 ? (
              <div className="py-24 text-center text-white/40">No bookings yet.</div>
            ) : (
              bookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`admin-booking-${b.id}`}
                  className="px-5 py-4 border-b border-white/10 last:border-b-0 grid grid-cols-12 gap-2 items-center text-sm"
                >
                  <div className="col-span-4">
                    <div className="font-heading font-bold">{b.title}</div>
                    <div className="overline text-[hsl(var(--primary))] mt-1">
                      {categoryLabel(b.category)} · {stateLabel(b.state)}
                    </div>
                  </div>
                  <div className="col-span-3 text-white/60">
                    {formatEventDate(b.event_date)} · {b.start_time}
                  </div>
                  <div className="col-span-3 font-medium">{b.booked_by_name || "—"}</div>
                  <div className="col-span-2 text-white/60">{b.booked_by_phone || "—"}</div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Event form modal */}
      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-5 overflow-y-auto">
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-[#22303C] border border-white/15 p-8 my-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-3xl tracking-tight">
                {editId ? "EDIT EVENT" : "NEW EVENT"}
              </h3>
              <button
                type="button"
                data-testid="close-form"
                onClick={() => setShowForm(false)}
                className="text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="overline text-white/50 block mb-2">Title</label>
                <input
                  data-testid="form-title"
                  required
                  value={form.title}
                  onChange={set("title")}
                  className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                />
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Category</label>
                <select
                  data-testid="form-category"
                  value={form.category}
                  onChange={set("category")}
                  className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">
                  State <span className="text-[hsl(var(--primary))]">*</span>
                </label>
                <select
                  data-testid="form-state"
                  required
                  value={form.state}
                  onChange={set("state")}
                  className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                >
                  <option value="" disabled>
                    Select a state…
                  </option>
                  {STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <p className="text-white/40 text-xs mt-1.5">
                  This is what the dashboard state filter uses — pick the state the event is held in.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-3 sm:col-span-1">
                  <label className="overline text-white/50 block mb-2">Date</label>
                  <input
                    data-testid="form-date"
                    type="date"
                    required
                    value={form.event_date}
                    onChange={set("event_date")}
                    className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  />
                </div>
                <div>
                  <label className="overline text-white/50 block mb-2">Start</label>
                  <input
                    data-testid="form-start"
                    type="time"
                    required
                    value={form.start_time}
                    onChange={set("start_time")}
                    className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  />
                </div>
                <div>
                  <label className="overline text-white/50 block mb-2">End</label>
                  <input
                    data-testid="form-end"
                    type="time"
                    value={form.end_time}
                    onChange={set("end_time")}
                    className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  />
                </div>
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Location</label>
                <input
                  data-testid="form-location"
                  required
                  value={form.location}
                  onChange={set("location")}
                  className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  placeholder="Austin, TX — Range 12"
                />
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Description</label>
                <textarea
                  data-testid="form-description"
                  rows={3}
                  value={form.description}
                  onChange={set("description")}
                  className="w-full bg-[#1A1A1A] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] resize-none"
                />
              </div>
            </div>

            <button
              data-testid="submit-event"
              type="submit"
              disabled={busy}
              className="w-full mt-6 bg-[hsl(var(--primary))] text-white overline py-4 hard-shadow-hover disabled:opacity-50"
            >
              {busy ? "Saving…" : editId ? "Update Event" : "Add To Schedule"}
            </button>
          </motion.form>
        </div>
      )}

      <Footer />
    </div>
  );
}
